import { useStore } from '@/store';
import { characteristicPointCost } from '@/lib/scores';
import type { Characteristics } from '@/types/character';
import { InfoHint } from '@/components/ui/InfoHint';

const CHARS: Array<{ key: keyof Characteristics; label: string }> = [
  { key: 'intelligence', label: 'Intelligence' },
  { key: 'perception', label: 'Perception' },
  { key: 'strength', label: 'Strength' },
  { key: 'stamina', label: 'Stamina' },
  { key: 'presence', label: 'Presence' },
  { key: 'communication', label: 'Communication' },
  { key: 'dexterity', label: 'Dexterity' },
  { key: 'quickness', label: 'Quickness' },
];

export function BasicsSection() {
  const c = useStore((s) => s.character!);
  const update = useStore((s) => s.updateCharacter);

  // Positive costs (spending on +N characteristics) and refunds (taking −N
  // to get extra points) are tracked separately — the net "spent" metric
  // can be negative when refunds outpace costs, which is confusing shown
  // on its own. Display both components.
  const positiveCost = CHARS.reduce((sum, row) => {
    const cost = characteristicPointCost(c.characteristics[row.key]);
    return cost > 0 ? sum + cost : sum;
  }, 0);
  const refund = CHARS.reduce((sum, row) => {
    const cost = characteristicPointCost(c.characteristics[row.key]);
    return cost < 0 ? sum + -cost : sum;
  }, 0);
  const net = positiveCost - refund;

  return (
    <div className="space-y-6">
      <section>
        <h2 className="section-title">Identity</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <input
              className="input-base"
              value={c.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="e.g. Vulcanus of Flambeau"
            />
          </Field>
          <Field label="Age">
            <input
              className="input-base"
              type="number"
              min={5}
              max={120}
              value={c.age ?? ''}
              onChange={(e) => update({ age: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
            />
          </Field>
          <Field label="Apparent age">
            <input
              className="input-base"
              type="number"
              min={0}
              max={200}
              value={c.apparentAge ?? ''}
              onChange={(e) =>
                update({ apparentAge: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })
              }
            />
          </Field>
          <Field label="Gender">
            <input
              className="input-base"
              value={c.gender ?? ''}
              onChange={(e) => update({ gender: e.target.value })}
            />
          </Field>
          <Field label="Nationality / Birthplace">
            <input
              className="input-base"
              value={c.nationality ?? ''}
              onChange={(e) => update({ nationality: e.target.value })}
            />
          </Field>
          <Field label="Native language">
            <input
              className="input-base"
              placeholder="e.g. Latin, Frankish, Arabic"
              value={c.nativeLanguage ?? ''}
              onChange={(e) => update({ nativeLanguage: e.target.value })}
            />
          </Field>
          <Field label="Size">
            <input
              className="input-base"
              type="number"
              min={-4}
              max={4}
              value={c.size ?? 0}
              onChange={(e) => update({ size: parseInt(e.target.value, 10) || 0 })}
            />
          </Field>
          <Field label="Covenant">
            <input
              className="input-base"
              value={c.covenant?.name ?? ''}
              onChange={(e) => update({ covenant: { ...c.covenant, name: e.target.value } })}
            />
          </Field>
          <Field label="Appearance" full>
            <textarea
              className="input-base min-h-[80px]"
              value={c.appearance ?? ''}
              onChange={(e) => update({ appearance: e.target.value })}
            />
          </Field>
        </div>
      </section>

      <section>
        <h2 className="section-title">
          Characteristics
          <InfoHint>
            <strong>Budget: 7 points.</strong> Each Characteristic ranges −3 to +3.
            Cost follows a triangular curve: +1 costs 1, +2 costs 3, +3 costs 6.
            Taking negative values <em>refunds</em> the same amount — so a −2
            gives you 3 extra points to spend on other Characteristics. Net
            (cost − refund) must not exceed 7.
          </InfoHint>
          <span
            className={`text-sm ml-2 ${net > 7 ? 'text-amber-300' : 'muted'}`}
          >
            {refund > 0
              ? `${positiveCost} cost − ${refund} refund = ${net} / 7`
              : `spent ${net} / 7`}
          </span>
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {CHARS.map((row) => {
            const val = c.characteristics[row.key];
            return (
              <div key={row.key} className="flex items-center gap-3 card">
                <div className="flex-1">
                  <div className="text-sm">{row.label}</div>
                  <div className="text-xs muted">cost: {characteristicPointCost(val)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="btn-secondary px-2 py-1 text-xs"
                    onClick={() =>
                      update((cur) => ({
                        ...cur,
                        characteristics: { ...cur.characteristics, [row.key]: Math.max(-3, val - 1) },
                      }))
                    }
                  >
                    −
                  </button>
                  <div className="w-10 text-center text-lg font-semibold">{val >= 0 ? `+${val}` : val}</div>
                  <button
                    className="btn-secondary px-2 py-1 text-xs"
                    onClick={() =>
                      update((cur) => ({
                        ...cur,
                        characteristics: { ...cur.characteristics, [row.key]: Math.min(3, val + 1) },
                      }))
                    }
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="section-title">
          Personality Traits
          <InfoHint>
            Short descriptors (e.g. <em>Brave +3, Pious +2</em>) that the
            storyteller invokes in roleplay checks. Score ranges −3..+3; three
            is a strong baseline. Grogs default to <em>Loyal</em>.
          </InfoHint>
        </h2>
        <div className="space-y-2">
          {c.personality.map((t, i) => (
            <div key={i} className="flex items-center gap-2 card">
              <input
                className="input-base flex-1"
                value={t.name}
                onChange={(e) =>
                  update((cur) => ({
                    ...cur,
                    personality: cur.personality.map((p, idx) => (idx === i ? { ...p, name: e.target.value } : p)),
                  }))
                }
                placeholder="Brave, Loyal, Curious..."
              />
              <input
                className="input-base w-20"
                type="number"
                min={-3}
                max={3}
                value={t.score}
                aria-label={`Score for ${t.name || 'personality trait'}`}
                onChange={(e) =>
                  update((cur) => ({
                    ...cur,
                    personality: cur.personality.map((p, idx) => (idx === i ? { ...p, score: parseInt(e.target.value, 10) || 0 } : p)),
                  }))
                }
              />
              <button
                className="btn-secondary px-2 py-1 text-xs"
                aria-label={`Remove personality trait${t.name ? ` ${t.name}` : ''}`}
                onClick={() =>
                  update((cur) => ({
                    ...cur,
                    personality: cur.personality.filter((_, idx) => idx !== i),
                  }))
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            className="btn-secondary"
            onClick={() =>
              update((cur) => ({ ...cur, personality: [...cur.personality, { name: '', score: 0 }] }))
            }
          >
            Add personality trait
          </button>
        </div>
      </section>

      <section>
        <h2 className="section-title">
          Reputations
          <InfoHint>
            How widely your character is known within a given realm
            (<em>Hermetic, Church, Local, Faerie…</em>). Score 1 means
            specialists in that realm might recognise your name; 5 is legendary.
            Usually built during play — 0–1 entries at creation is fine.
          </InfoHint>
        </h2>
        <div className="space-y-2">
          {c.reputations.map((r, i) => (
            <div key={i} className="flex items-center gap-2 card">
              <input
                className="input-base flex-1"
                placeholder="Name"
                value={r.name}
                onChange={(e) =>
                  update((cur) => ({
                    ...cur,
                    reputations: cur.reputations.map((p, idx) => (idx === i ? { ...p, name: e.target.value } : p)),
                  }))
                }
              />
              <input
                className="input-base w-40"
                placeholder="Realm (Hermetic, Church, …)"
                value={r.realm}
                onChange={(e) =>
                  update((cur) => ({
                    ...cur,
                    reputations: cur.reputations.map((p, idx) => (idx === i ? { ...p, realm: e.target.value } : p)),
                  }))
                }
              />
              <input
                className="input-base w-20"
                type="number"
                min={0}
                max={5}
                value={r.score}
                aria-label={`Score for ${r.name || 'reputation'}`}
                onChange={(e) =>
                  update((cur) => ({
                    ...cur,
                    reputations: cur.reputations.map((p, idx) => (idx === i ? { ...p, score: parseInt(e.target.value, 10) || 0 } : p)),
                  }))
                }
              />
              <button
                className="btn-secondary px-2 py-1 text-xs"
                aria-label={`Remove reputation${r.name ? ` ${r.name}` : ''}`}
                onClick={() =>
                  update((cur) => ({ ...cur, reputations: cur.reputations.filter((_, idx) => idx !== i) }))
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            className="btn-secondary"
            onClick={() =>
              update((cur) => ({ ...cur, reputations: [...cur.reputations, { name: '', realm: '', score: 0 }] }))
            }
          >
            Add reputation
          </button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={`${full ? 'sm:col-span-2' : ''} flex flex-col gap-1 text-sm`}>
      <span className="muted">{label}</span>
      {children}
    </label>
  );
}
